import { apiClient } from './client'

export type PlatformScopeType = 'global' | 'org' | 'entry'

export interface PlatformPermissionItem {
  key: string
  name: string
  description: string | null
}

export interface PlatformRoleItem {
  key: string
  name: string
  description: string | null
  is_system: boolean
  permissions: string[]
}

export interface PlatformUserRef {
  id: string
  email: string
  full_name: string
  role: string
  org_id: string | null
  org_code: string | null
  org_name: string | null
}

export interface RoleAssignmentCreate {
  user_id: string
  role_key: string
  scope_type: PlatformScopeType
  scope_id?: string | null
  reason: string
  expires_at?: string | null
}

export interface RoleAssignmentItem {
  id: string
  user: PlatformUserRef
  role_key: string
  role_name: string
  scope_type: PlatformScopeType
  scope_id: string | null
  scope_label: string
  reason: string | null
  assigned_by: string | null
  assigned_by_email: string | null
  expires_at: string | null
  revoked_at: string | null
  created_at: string
  active: boolean
}

export interface TemporaryElevationCreate {
  user_id: string
  scope_type: PlatformScopeType
  scope_id?: string | null
  permissions: string[]
  reason: string
  expires_at: string
}

export interface TemporaryElevationItem {
  id: string
  user: PlatformUserRef
  scope_type: PlatformScopeType
  scope_id: string | null
  scope_label: string
  permissions: string[]
  reason: string
  approved_by: string | null
  approved_by_email: string | null
  expires_at: string
  revoked_at: string | null
  created_at: string
  active: boolean
}

export interface PlatformRbacListParams {
  user_id?: string
  active_only?: boolean
}

export const platformRbacAPI = {
  listPermissions: async (): Promise<PlatformPermissionItem[]> => {
    const response = await apiClient.get<PlatformPermissionItem[]>('/platform-rbac/permissions')
    return response.data
  },

  listRoles: async (): Promise<PlatformRoleItem[]> => {
    const response = await apiClient.get<PlatformRoleItem[]>('/platform-rbac/roles')
    return response.data
  },

  listAssignments: async (params: PlatformRbacListParams = {}): Promise<RoleAssignmentItem[]> => {
    const response = await apiClient.get<RoleAssignmentItem[]>('/platform-rbac/assignments', {
      params,
    })
    return response.data
  },

  createAssignment: async (data: RoleAssignmentCreate): Promise<RoleAssignmentItem> => {
    const response = await apiClient.post<RoleAssignmentItem>('/platform-rbac/assignments', data)
    return response.data
  },

  revokeAssignment: async (assignmentId: string): Promise<RoleAssignmentItem> => {
    const response = await apiClient.post<RoleAssignmentItem>(
      `/platform-rbac/assignments/${assignmentId}/revoke`,
    )
    return response.data
  },

  listElevations: async (params: PlatformRbacListParams = {}): Promise<TemporaryElevationItem[]> => {
    const response = await apiClient.get<TemporaryElevationItem[]>('/platform-rbac/elevations', {
      params,
    })
    return response.data
  },

  createElevation: async (data: TemporaryElevationCreate): Promise<TemporaryElevationItem> => {
    const response = await apiClient.post<TemporaryElevationItem>('/platform-rbac/elevations', data)
    return response.data
  },

  revokeElevation: async (elevationId: string): Promise<TemporaryElevationItem> => {
    const response = await apiClient.post<TemporaryElevationItem>(
      `/platform-rbac/elevations/${elevationId}/revoke`,
    )
    return response.data
  },
}
